
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { Box, Button, FormLabel } from "@mui/material";
import { ContentView } from "../../components/content-view/ContentView";
import { ContentContainer } from "../../components/content-container/ContentContainer";
import { ContentSection } from "../../components/content-section/ContentSection";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import OptionsSideSheet from "../../components/options-side-sheet/OptionsSideSheet";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import useAlarmNotificationAdminReportState from "./AlarmNotificationAdminReportState";
import { useAlarmNotificationAdminReport } from "../../queries/admin-reports/UseAlarmNotificationsAdminReport";
import { useCallback, useEffect } from "react";
import AlarmNotificationAdminReportUtils from "./AlarmNotificationAdminReportUtils";
import { DateRange } from "@mui/icons-material";
import { t } from "i18next";
import { CustomDurationDialog, CustomDurationDialogValues, bindCustomDurationDialog, useCustomDurationDialogState } from "../../components/custom-duration-dialog/CustomDurationDialog";
import LoadingZone from "../../components/loading-zone/LoadingZone";
import NoData from "../../components/no-data/NoData";
import { useProgressContext } from "../../contexts/progress-context/ProgressContext";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import _ from "lodash";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import UUID from "../../common/utilities/Uuid";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ActionTypeEnum, StatusEnum } from "../../common/IExportNotificationMsg";
import AdminReportDataService from "../../services/admin-report-data-service/AdminReportDataService";
import { ActionMenuItem, BaseActionEnum  } from "../../components/action-menu/ActionMenuItem";
import { RestError } from "../../services/rest-service/RestService";
import { useOrganizations } from "../../queries/organizations/UseOrganizations";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useGroup } from "../../queries/groups/UseGroup";
import { IOrganization } from "../../services/organization-data-service/Models";
import { SortChangedEvent } from "ag-grid-community";
import DefaultGrid from "../../components/default-grid/DefaultGrid";
import { AutoSizeStrategyEnum } from "../../components/default-grid/InternalGrid";


export default function AlarmNotificationAdminReportView() {

    const [userState] = useUserContext();
    const routeContext = useRouteContext();
    const [, setAppNotificationState] = useAppNotificationContext();
    const durationDialog = useCustomDurationDialogState();

    const [progressState, actionStarted] = useProgressContext();


    const {
        state,
        setOrganization,
        setDuration,
        setGridData,
        setActions,
        setDownloadUid,
        setRun,
        setSortParameters,
        resetState
    } = useAlarmNotificationAdminReportState();


    const { data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number
    });

    const { data: organizations } = useQueryNotifications(useOrganizations)({
        includeAllOption: true
    });

    const organization = organizations?.find(o => o.id === (state.organizationID ?? group?.organizationID));

    const { data, status } = useQueryNotifications(useAlarmNotificationAdminReport)({
        contextGroupID: organization?.peerGroupID ?? routeContext.groupID as number,
        startDate: state.durationOptions.duration.startDate,
        endDate: state.durationOptions.duration.endDate,
        enabled: state.run
    });


    const noDataTitle = t(!state.run ? 'alarmNotificationReports.instructionTitle' : 'noData.title');
    const noDataText = state.run && (state.gridData === undefined || (state.gridData !== undefined && state.gridData.rows.length === 0)) ?
        t('alarmNotificationReports.nodataMessage') :
        !state.run && state.gridData === undefined ?
            t('alarmNotificationReports.runReportHint') :
            undefined;


    const updateActions = useCallback((running: boolean) => {

        const actions = _.cloneDeep(state.actions);
        const runAction = actions.find(o => o.action === BaseActionEnum.Run);
        if (runAction) {
            runAction.disabled = running;
            setActions(actions);
        }
    }, [
        state.actions,
        setActions,
    ]);

    const onExport = (mimeType: ApplicationMimeTypesEnum) => {

        setRun(false);

        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group,
            title: 'Alarm Notification Admin Report',
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        try {

            AdminReportDataService.exportAlarmNotifications(uid,
                routeContext.groupID as number,
                state.durationOptions.duration.startDate,
                state.durationOptions.duration.endDate,
                mimeType,
                state.sortParameters
            );

            const items = [...state.actions];
            const exportAction = items.find(o => o.action === BaseActionEnum.Export);
            if (exportAction) {
                exportAction.disabled = true;
            }
            setActions(items);
        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    };

    async function onCustomDuration() {
        const result = await durationDialog.open(state.durationOptions);

        if (result.reason === "ok") {
            const values = result.values as CustomDurationDialogValues;

            setDuration(values);
        }
    }

    async function onRunReport() {
        setRun(true);
        updateActions(true);
    }

    function onExecuteAction(menuItem: ActionMenuItem) {

        const item = menuItem.action as BaseActionEnum;;

        switch (item) {
            case BaseActionEnum.Refresh:
                onRunReport();
                break;

            case BaseActionEnum.Run:
                onRunReport();
                break;

            case BaseActionEnum.PDF:
                onExport(ApplicationMimeTypesEnum.PDF);
                break;

            case BaseActionEnum.Excel:
                onExport(ApplicationMimeTypesEnum.MSExcel);
                break;
        }
    }

    function onResetOptions() {
        resetState();
    }

    function onOrganizationChanged(item: IOrganization) {
        setOrganization(item.id);
    }

    function onSortChanged(ev: SortChangedEvent) {

        const columnState = ev.api.getColumnState();

        const sortItem = columnState.find(o => !!o.sort);

        if (sortItem) {
            setSortParameters({
                propertyName: sortItem.colId,
                sortAscending: sortItem.sort === 'asc'
            });
        }
    }

    useEffect(() => {

        if (data && state.run) {

            const cols = AlarmNotificationAdminReportUtils.createGridColumns();
            const rows = AlarmNotificationAdminReportUtils.createGridRows(data);

            setGridData({
                cols,
                rows
            });

            setRun(false);
            updateActions(false);
        }
    }, [
        data,
        state.run,
        setGridData,
        setRun,
        updateActions
    ]);


    const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    useEffect(() => {

        if (progress?.uid === state.downloadUid &&
            (progress?.notificationMsg.status === StatusEnum.Completed ||
                progress?.notificationMsg.status === StatusEnum.Failed)) {
            setDownloadUid(undefined);
        }
    }, [
        progress,
        state.downloadUid,
        setDownloadUid
    ]);


    useEffect(() => {

        const items = _.cloneDeep(state.actions);

        const exportAction = items.find(o => o.action === BaseActionEnum.Export);

        if (exportAction) {
            exportAction.disabled = progress && progress.uid === state.downloadUid;
        }

        // if (!_.isEqual(state.actions, items)) {
            setActions(items);
        // }

    }, [
        state.actions,
        progress,
        state.downloadUid,
        setActions
    ]);

    useEffect(() => {
        if (organization?.id && organization?.id !== state.organizationID) {
            setOrganization(organization.id);
        }
    }, [
        organization?.id,
        organization?.peerGroupID,
        state.organizationID,
        setOrganization
    ]);

    return (
        <ContentView>
            <ContextualAppBar
                hideBreadcrumb={true}
                actions={state.actions}
                viewOptionState={state.viewOptionState}
                onExecuteAction={onExecuteAction}
                showOrganizationPicker={true}
                onOrganizationSelected={onOrganizationChanged}
                organizationID={state.organizationID}
            />

            <LoadingZone isLoading={status === 'loading'}>
                <ContentContainer>
                    <ContentSection>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                margin: '1rem',
                                overflow: "hidden"
                            }}>

                            {noDataText &&
                                <NoData
                                    title={noDataTitle}
                                    text={noDataText}
                                />
                            }

                            {state.gridData && state.gridData.rows.length > 0 &&

                                <DefaultGrid
                                    onSortChanged={onSortChanged}
                                    suppressColumnVirtualisation={true}
                                    suppressMovableColumns={true}
                                    suppressMultiSort={true}
                                    rowData={state.gridData.rows}
                                    defaultColDef={{
                                        sortable: false,
                                        filter: "agMultiColumnFilter",
                                        resizable: true,
                                        // SPR 04-08-2025 - Removed autoHeight and wrapText from defaultColDef
                                        // Use multiline column type in column definitions, instead
                                        cellStyle: {
                                            wordBreak: 'normal',
                                        }
                                    }}
                                    columnDefs={state.gridData.cols}
                                    autoSizeStrategy={AutoSizeStrategyEnum.None}
                                />
                            }
                        </Box>
                    </ContentSection>

                    <OptionsSideSheet onReset={onResetOptions}>
                        <FormLabel component="legend">{t('customDuration.duration')}</FormLabel>
                        <Button variant='text'
                            startIcon={<DateRange />}
                            sx={{ justifyContent: 'flex-start' }}
                            onClick={onCustomDuration}>
                            {state.durationLabel}
                        </Button>
                    </OptionsSideSheet>
                </ContentContainer>
            </LoadingZone>

            <CustomDurationDialog
                hideDefault={true}
                value={state.durationOptions}
                {...bindCustomDurationDialog(durationDialog)}
            />

        </ContentView>
    );
}
